/**
 * Home: the app's front door. Today first (the route she opens the app for), then every other
 * job with its live status, so she always knows what's happening and what's next.
 */
import { ChevronRight, CalendarDays, FileBarChart, PackageCheck, Settings, TriangleAlert } from "lucide-react";
import type { CalendarDate } from "@hitl/rotations-domain";
import type { DayState } from "../state/session";
import type { SyncState } from "../state/eventLog";
import { SyncBadge, groupByClient } from "../scan/parts";
import { formatDay } from "../scan/copy";
import { weekdayName } from "../plan/PlanScreen";

export interface HomeStatus {
  readonly today: CalendarDate;
  readonly hour: number;
  readonly syncState: SyncState;
  readonly pending: number;
  readonly route?: { readonly name: string; readonly day: DayState };
  readonly load?: { readonly loaded: number; readonly total: number; readonly on: CalendarDate };
  readonly nextPlan?: { readonly on: CalendarDate; readonly routeName: string };
  readonly planProblems: number;
  readonly alerts: readonly string[];
}

function greeting(hour: number): string {
  return hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
}

export function Home(props: {
  status: HomeStatus;
  onTrack: () => void;
  onLoad: () => void;
  onPlan: () => void;
  onReports: () => void;
  /** Absent until Settings ships: the row says so instead of leading nowhere. */
  onSettings?: () => void;
}) {
  const { status } = props;
  const route = status.route;
  const done = route ? route.day.spots.filter((s) => s.done).length : 0;
  const total = route ? route.day.spots.length : 0;
  const next = route && route.day.nextOpenIndex !== null ? route.day.spots[route.day.nextOpenIndex] : undefined;
  const action = !route ? "Plan a route for today" : done === 0 ? "Start route" : done === total ? "Review today" : "Continue route";

  return (
    <main className="home">
      <header className="home__top">
        <div>
          <h1>{greeting(status.hour)}</h1>
          <p>
            {weekdayName(status.today)} {formatDay(status.today)}
          </p>
        </div>
        <SyncBadge state={status.syncState} pending={status.pending} />
      </header>

      <section className="today" aria-label="Today">
        {route ? (
          <>
            <h2 className="today__route">{route.name}</h2>
            <p className="today__count">
              <b>
                {done} of {total}
              </b>{" "}
              spots done{done === total ? " · day done" : ""}
            </p>
            <div className="today__bar" aria-hidden>
              {groupByClient(route.day.spots).map((g, i) => (
                <span key={`${g.clientId}-${i}`} className="today__stop">
                  {g.spots.map((s) => (
                    <span key={s.slotId} className={s.done ? "is-done" : ""} />
                  ))}
                </span>
              ))}
            </div>
            {next && (
              <p className="today__next">
                Next: {next.clientName}, {next.spotLabel}
              </p>
            )}
          </>
        ) : (
          <>
            <h2 className="today__route">No route today</h2>
            <p className="today__next">Place one of your routes on today in the planner.</p>
          </>
        )}
        <button className="btn btn--light btn--block today__go" onClick={route ? props.onTrack : props.onPlan}>
          {action}
        </button>
      </section>

      <nav className="jobs" aria-label="Everything else">
        <button className="job" onClick={props.onLoad} disabled={!status.load}>
          <PackageCheck aria-hidden size={22} />
          <span className="job__name">Load the car</span>
          <span className="job__status">
            {!status.load
              ? "No route to load"
              : status.load.total > 0 && status.load.loaded === status.load.total
                ? "Car loaded"
                : `${status.load.loaded} of ${status.load.total} in the car`}
          </span>
          <ChevronRight aria-hidden size={18} />
        </button>
        <button className="job" onClick={props.onPlan}>
          <CalendarDays aria-hidden size={22} />
          <span className="job__name">Plan the week</span>
          <span className="job__status">
            {status.planProblems > 0 && (
              <span className="job__alert">
                <TriangleAlert aria-hidden size={14} />
                {status.planProblems} to fix ·{" "}
              </span>
            )}
            {status.nextPlan ? `Next: ${weekdayName(status.nextPlan.on).slice(0, 3)} · ${status.nextPlan.routeName}` : "Nothing planned"}
          </span>
          <ChevronRight aria-hidden size={18} />
        </button>
        <button className="job" onClick={props.onReports}>
          <FileBarChart aria-hidden size={22} />
          <span className="job__name">Reports</span>
          <span className="job__status">
            {status.alerts.length > 0 ? (
              <span className="job__alert">
                <TriangleAlert aria-hidden size={14} />
                {status.alerts[0]}
                {status.alerts.length > 1 ? ` · +${status.alerts.length - 1} more` : ""}
              </span>
            ) : (
              "Assets, clients, stock and month"
            )}
          </span>
          <ChevronRight aria-hidden size={18} />
        </button>
        {props.onSettings ? (
          <button className="job" onClick={props.onSettings}>
            <Settings aria-hidden size={22} />
            <span className="job__name">Settings</span>
            <span className="job__status">Pots, clients, rules and people</span>
            <ChevronRight aria-hidden size={18} />
          </button>
        ) : (
          <div className="job job--off" aria-disabled="true">
            <Settings aria-hidden size={22} />
            <span className="job__name">Settings</span>
            <span className="job__status">Coming in the next update</span>
          </div>
        )}
      </nav>
    </main>
  );
}
